import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Zap,
  Shield,
  Brain,
  Target,
  TrendingUp,
  Users,
  Code2,
  Play,
  Terminal,
  Check,
  ChevronRight,
  Gauge,
  BarChart3,
  DollarSign,
  Activity,
  Eye,
  EyeOff,
  Layers,
  Receipt,
  FlaskConical,
  GitBranch,
  Cpu,
  FileText,
  AlertTriangle,
  CircleDollarSign,
  PieChart,
  ArrowUpRight,
  Lock,
  Clock,
  Settings2,
  Globe,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

const stagger = {
  visible: { transition: { staggerChildren: 0.08 } },
};

const CODE_INSTRUMENT = `import { AgentMargins } from "@shadow/margins";

const margins = new AgentMargins({ apiKey: "sk_..." });

// Wrap any LLM call — tokens, latency, cost captured automatically
const result = await margins.track({
  workflow: "loan_origination",
  user: "user_8f3k",
  agent: "credit_analyzer",
}, async () => {
  return await openai.chat.completions.create({
    model: "gpt-4o",
    messages: [{ role: "user", content: prompt }],
  });
});

// Track non-LLM costs too
await margins.trackCost({
  workflow: "loan_origination",
  user: "user_8f3k",
  service: "experian_credit_check",
  cost_usd: 3.50,
});`;

const CODE_PRICE_INVOICE = `// Define pricing rules per client (or use the dashboard)
await margins.pricing.create({
  client: "acme_corp",
  rules: [
    { metric: "llm_tokens",  rate: 0.003, per: 1000 },
    { metric: "tool_calls",  rate: 0.05,  per: "call" },
    { metric: "rag_queries", rate: 0.02,  per: "query" },
  ],
});

// Different pricing for a different client
await margins.pricing.create({
  client: "beta_inc",
  rules: [
    { metric: "llm_tokens",  rate: 0.002, per: 1000 },
    { metric: "workflow_run", rate: 1.50,  per: "run" },
  ],
});

// Invoices generate automatically at billing cycle end
// → Stripe / Razorpay integration included`;

const PIPELINE_STEPS = [
  {
    label: "Instrument",
    desc: "One SDK call wraps any LLM call, tool use, or external API. Auto-captures tokens, latency, and cost.",
    icon: Code2,
  },
  {
    label: "Attribute",
    desc: "Costs roll up by user, workflow, agent, team, or any custom dimension. Real-time, not batch.",
    icon: GitBranch,
  },
  {
    label: "Price",
    desc: "Define pricing rules per client, per workflow, per model. Simulate before shipping. No code deploys.",
    icon: DollarSign,
  },
  {
    label: "Invoice",
    desc: "Generate usage-based invoices automatically. Stripe/Razorpay integration. Finance dashboard with drill-down.",
    icon: Receipt,
  },
];

const COMPARISON_DIMENSIONS = [
  { label: "LLM Cost Tracking",       helicone: true,  metronome: false,     revenium: true,  margins: true },
  { label: "Non-LLM Costs (APIs, tools)", helicone: false, metronome: "Generic", revenium: true, margins: true },
  { label: "Usage-Based Invoicing",    helicone: false, metronome: true,      revenium: false, margins: true },
  { label: "Per-Client Pricing Rules", helicone: false, metronome: "Limited", revenium: false, margins: true },
  { label: "Pricing Experimentation",  helicone: false, metronome: "Orb only", revenium: false, margins: true },
  { label: "Finance Dashboard",        helicone: false, metronome: true,      revenium: "Partial", margins: true },
  { label: "AI-Native SDK",            helicone: true,  metronome: false,     revenium: "Partial", margins: true },
];

function ComparisonCell({ value }) {
  if (value === true) return <Check className="w-4 h-4 text-emerald-500 mx-auto" />;
  if (value === false) return <div className="w-4 h-0.5 bg-gray-300 rounded mx-auto" />;
  return <span className="text-xs text-amber-600 font-medium">{value}</span>;
}

export default function AgentMarginsPage() {
  const [activeTab, setActiveTab] = useState("instrument");

  return (
    <div className="min-h-screen bg-white antialiased">
      {/* Nav */}
      <header className="fixed top-0 w-full bg-white/90 backdrop-blur-md z-50 border-b border-gray-100">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-3 h-3 bg-black rounded-full" />
            <span className="text-lg font-semibold tracking-tight">
              <span className="text-gray-800">SHADOW</span>
              <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">LABS</span>
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <a href="#problem" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Problem</a>
            <a href="#how-it-works" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">How It Works</a>
            <a href="#compare" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Compare</a>
            <a href="#pricing" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Pricing</a>
            <Link to="/conversational-intelligence" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">API</Link>
            <a
              href="https://wa.me/919582819710?text=Hi%2C%20I%27m%20interested%20in%20Agent%20Margins"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#0f172a] text-white px-5 py-2 text-sm rounded-lg hover:bg-[#1e293b] transition-all font-medium"
            >
              Get Early Access
            </a>
          </div>
        </nav>
      </header>

      {/* ════════════════════════════════════════════
          HERO
      ════════════════════════════════════════════ */}
      <section className="relative pt-32 sm:pt-40 pb-20 sm:pb-28 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(120,119,198,0.08),transparent)]" />

        <motion.div
          className="max-w-4xl mx-auto text-center relative"
          initial="hidden"
          animate="visible"
          variants={stagger}
        >
          <motion.div variants={fadeUp} custom={0}>
            <span className="inline-flex items-center gap-2 bg-gray-100 text-gray-600 text-xs font-medium px-3.5 py-1.5 rounded-full mb-6">
              <BarChart3 className="w-3.5 h-3.5" />
              Agent Margins
            </span>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            custom={1}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.08] text-gray-900 mb-6"
          >
            Know your margins.
            <br />
            <span className="bg-gradient-to-r from-gray-900 via-gray-500 to-gray-900 bg-clip-text text-transparent">
              Bill with confidence.
            </span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            custom={2}
            className="text-lg sm:text-xl text-gray-500 max-w-2xl mx-auto mb-10 leading-relaxed"
          >
            Cost attribution, usage-based invoicing, and dynamic pricing
            for Agentic AI &mdash; in one SDK.{" "}
            <span className="text-gray-900 font-medium">
              From "what did this workflow cost?" to "charge the customer" in three lines.
            </span>
          </motion.p>

          <motion.div variants={fadeUp} custom={3} className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://wa.me/919582819710?text=Hi%2C%20I%27m%20interested%20in%20Agent%20Margins"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-[#0f172a] text-white px-8 py-4 text-sm rounded-xl hover:bg-[#1e293b] transition-all font-medium flex items-center gap-2 shadow-lg shadow-gray-900/10 hover:shadow-xl hover:shadow-gray-900/20 hover:scale-[1.02]"
            >
              Get Early Access
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </a>
            <a
              href="#shadow-reference"
              className="group text-gray-600 hover:text-gray-900 px-8 py-4 text-sm font-medium flex items-center gap-2 transition-colors"
            >
              <Play className="w-4 h-4" />
              See how Shadow uses it
            </a>
          </motion.div>

          <motion.div variants={fadeUp} custom={4} className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-gray-400">
            <span className="flex items-center gap-1.5"><Code2 className="w-3 h-3" /> 3-line integration</span>
            <span className="flex items-center gap-1.5"><Activity className="w-3 h-3" /> Real-time cost streaming</span>
            <span className="flex items-center gap-1.5"><PieChart className="w-3 h-3" /> Finance-team dashboard</span>
            <span className="flex items-center gap-1.5"><FlaskConical className="w-3 h-3" /> Pricing experiments</span>
          </motion.div>
        </motion.div>
      </section>

      {/* ════════════════════════════════════════════
          THE PROBLEM
      ════════════════════════════════════════════ */}
      <section id="problem" className="py-20 sm:py-28 px-4 bg-gray-50 border-y border-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs text-gray-400 uppercase tracking-widest mb-4">The Problem</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-5">
              AI companies are flying blind<br />on unit economics.
            </h2>
            <p className="text-gray-500 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
              Multiple LLM calls, external APIs, RAG pipelines, tool use &mdash; costs are distributed 
              across dozens of services. Nobody knows what a single workflow actually costs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: EyeOff,
                title: "Blind Costs",
                stat: "<1%",
                statLabel: "of agent cost is LLM tokens",
                desc: "External APIs, RAG queries, tool calls, and compute make up the rest. A loan origination agent spends $0.30 on tokens but $50–85 total. Most companies can't attribute any of it.",
                color: "from-rose-500 to-orange-500",
              },
              {
                icon: FileText,
                title: "Manual Invoicing",
                stat: "45%",
                statLabel: "of AI companies invoice manually",
                desc: "Usage-based pricing adopted but invoiced via spreadsheets. Finance teams reconcile at month-end. Revenue leaks through the cracks. Disputes go unresolved.",
                color: "from-amber-500 to-yellow-500",
              },
              {
                icon: Lock,
                title: "One-Size Pricing",
                stat: "0",
                statLabel: "pricing experiments run",
                desc: "Same price for every client regardless of usage patterns. No ability to A/B test pricing, offer volume discounts, or run per-client experiments without a code deploy.",
                color: "from-violet-500 to-indigo-500",
              },
            ].map((card, i) => {
              const Icon = card.icon;
              return (
                <motion.div
                  key={card.title}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-30px" }}
                  variants={fadeUp}
                  custom={i}
                  className="bg-white rounded-2xl border border-gray-100 p-7 hover:shadow-lg hover:border-gray-200 transition-all"
                >
                  <div className="w-11 h-11 rounded-xl bg-gray-100 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-gray-700" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{card.title}</h3>
                  <div className="mb-4">
                    <span className={`text-3xl font-bold bg-gradient-to-r ${card.color} bg-clip-text text-transparent`}>
                      {card.stat}
                    </span>
                    <p className="text-xs text-gray-400 mt-1">{card.statLabel}</p>
                  </div>
                  <p className="text-sm text-gray-500 leading-relaxed">{card.desc}</p>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={3}
            className="mt-12 bg-[#0f172a] rounded-2xl p-6 sm:p-8"
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-white font-semibold mb-1">The Forrester Warning</p>
                <p className="text-gray-400 text-sm leading-relaxed">
                  "Enterprises will defer 25% of planned AI spend into 2027" due to inability to answer 
                  whether AI workflows produce more value than they consume. Cost visibility isn't a 
                  nice-to-have &mdash; it's the prerequisite for AI budget survival.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          HOW IT WORKS (Pipeline)
      ════════════════════════════════════════════ */}
      <section id="how-it-works" className="py-20 sm:py-28 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs text-gray-400 uppercase tracking-widest mb-4">How It Works</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-5">
              Four steps. Full-stack margins.
            </h2>
            <p className="text-gray-500 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
              From instrumenting your first LLM call to sending your first automated invoice &mdash; 
              in under an hour.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PIPELINE_STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.label}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-30px" }}
                  variants={fadeUp}
                  custom={i}
                  className="relative"
                >
                  <div className="bg-white rounded-2xl border border-gray-100 p-6 h-full hover:border-gray-200 hover:shadow-lg transition-all">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 rounded-lg bg-gray-900 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                        {i + 1}
                      </div>
                      <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center">
                        <Icon className="w-4.5 h-4.5 text-gray-700" />
                      </div>
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 mb-2">{step.label}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{step.desc}</p>
                  </div>
                  {i < PIPELINE_STEPS.length - 1 && (
                    <div className="hidden lg:flex absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                      <ChevronRight className="w-5 h-5 text-gray-300" />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          DEVELOPER EXPERIENCE (Code Preview)
      ════════════════════════════════════════════ */}
      <section className="py-16 sm:py-20 px-4 bg-gray-50 border-y border-gray-100">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs text-gray-400 uppercase tracking-widest mb-4">Developer Experience</p>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">
              Three lines to track. Zero lines to invoice.
            </h2>
          </div>

          <div className="bg-[#0f172a] rounded-2xl overflow-hidden shadow-2xl shadow-gray-900/20">
            <div className="flex border-b border-white/10">
              <button
                onClick={() => setActiveTab("instrument")}
                className={`px-6 py-3 text-sm font-medium transition-colors ${
                  activeTab === "instrument"
                    ? "text-white bg-white/10"
                    : "text-gray-400 hover:text-gray-200"
                }`}
              >
                <span className="flex items-center gap-2">
                  <Terminal className="w-3.5 h-3.5" />
                  Instrument
                </span>
              </button>
              <button
                onClick={() => setActiveTab("price")}
                className={`px-6 py-3 text-sm font-medium transition-colors ${
                  activeTab === "price"
                    ? "text-white bg-white/10"
                    : "text-gray-400 hover:text-gray-200"
                }`}
              >
                <span className="flex items-center gap-2">
                  <DollarSign className="w-3.5 h-3.5" />
                  Price + Invoice
                </span>
              </button>
            </div>
            <div className="p-5 sm:p-6">
              <pre className="text-sm font-mono text-gray-300 leading-relaxed overflow-x-auto">
                <code>{activeTab === "instrument" ? CODE_INSTRUMENT : CODE_PRICE_INVOICE}</code>
              </pre>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: Zap, title: "Auto-capture", desc: "Tokens, latency, model, cost — captured from the LLM response automatically." },
              { icon: Layers, title: "Any cost source", desc: "LLM calls, external APIs, tool use, compute, human review — one unified schema." },
              { icon: Settings2, title: "Dashboard or code", desc: "Define pricing rules in the dashboard or via SDK. Non-technical teams can iterate independently." },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  custom={i}
                  className="bg-white rounded-xl border border-gray-100 p-5 hover:shadow-md transition-all"
                >
                  <Icon className="w-5 h-5 text-gray-700 mb-3" />
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">{item.title}</h4>
                  <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          FINANCE DASHBOARD (Mock UI)
      ════════════════════════════════════════════ */}
      <section className="py-20 sm:py-28 px-4 bg-[#0f172a]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
            >
              <motion.p variants={fadeUp} className="text-xs text-gray-500 uppercase tracking-widest mb-4">
                For Finance Teams
              </motion.p>
              <motion.h2 variants={fadeUp} custom={1} className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-5">
                Your CFO will<br />actually love this.
              </motion.h2>
              <motion.p variants={fadeUp} custom={2} className="text-gray-400 text-base leading-relaxed mb-8">
                No more spreadsheet reconciliation. No more "we think it costs about..." conversations. 
                Real-time margin visibility, automated invoicing, and drill-down from invoice line item 
                to individual LLM call.
              </motion.p>
              <motion.div variants={fadeUp} custom={3} className="space-y-5">
                {[
                  { icon: BarChart3, title: "Revenue by Client", desc: "Real-time revenue attribution per client, per workflow, per billing period." },
                  { icon: TrendingUp, title: "Margin per Workflow", desc: "Cost vs. revenue for every workflow. Spot negative-margin agents instantly." },
                  { icon: CircleDollarSign, title: "Automated Invoicing", desc: "Usage-based invoices generated and sent via Stripe or Razorpay. Zero manual work." },
                  { icon: AlertTriangle, title: "Margin Alerts", desc: "Get notified when a client's margin drops below threshold or costs spike unexpectedly." },
                  { icon: Receipt, title: "Audit Trail", desc: "Every cost event linked to its source. Drill from invoice line item to the exact API call." },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <motion.div key={item.title} variants={fadeUp} custom={i + 3} className="flex items-start gap-4">
                      <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon className="w-4 h-4 text-gray-300" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-white mb-0.5">{item.title}</h4>
                        <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            </motion.div>

            {/* Mock Dashboard */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={2}
            >
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5 sm:p-6">
                <div className="flex items-center justify-between mb-5 pb-4 border-b border-white/10">
                  <div className="flex items-center gap-2">
                    <PieChart className="w-4 h-4 text-gray-400" />
                    <span className="text-xs font-mono text-gray-400">margin_dashboard</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-[10px] text-emerald-400 font-mono">live</span>
                  </div>
                </div>

                {/* Metric Cards */}
                <div className="grid grid-cols-3 gap-3 mb-5">
                  {[
                    { label: "Revenue MTD", value: "$47.2K", sub: "+12% MoM" },
                    { label: "Avg Margin", value: "68%", sub: "across clients" },
                    { label: "Invoices Sent", value: "142", sub: "this cycle" },
                  ].map((m) => (
                    <div key={m.label} className="bg-white/5 rounded-lg p-3 border border-white/5">
                      <p className="text-lg font-bold text-white">{m.value}</p>
                      <p className="text-[10px] text-gray-500">{m.label}</p>
                      <p className="text-[9px] text-emerald-400 mt-0.5">{m.sub}</p>
                    </div>
                  ))}
                </div>

                {/* Revenue by Client */}
                <div className="bg-white/5 rounded-lg p-4 border border-white/5 mb-5">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-3 font-medium">Revenue by Client</p>
                  <div className="space-y-2.5">
                    {[
                      { name: "Acme Corp", revenue: "$18.4K", cost: "$5.9K", margin: "68%", pct: 85 },
                      { name: "Beta Inc", revenue: "$12.1K", cost: "$4.2K", margin: "65%", pct: 60 },
                      { name: "Gamma Labs", revenue: "$9.8K", cost: "$2.1K", margin: "79%", pct: 45 },
                      { name: "Delta AI", revenue: "$6.9K", cost: "$3.8K", margin: "45%", pct: 30 },
                    ].map((c) => (
                      <div key={c.name}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-300">{c.name}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-[10px] text-gray-500">{c.cost} cost</span>
                            <span className="text-xs font-mono text-white">{c.revenue}</span>
                          </div>
                        </div>
                        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full"
                            style={{ width: `${c.pct}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Invoice Status */}
                <div className="bg-white/5 rounded-lg p-4 border border-white/5">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-3 font-medium">Invoice Status</p>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { status: "Paid", count: "128", color: "bg-emerald-400" },
                      { status: "Pending", count: "11", color: "bg-amber-400" },
                      { status: "Overdue", count: "3", color: "bg-rose-400" },
                    ].map((s) => (
                      <div key={s.status} className="text-center">
                        <div className="flex items-center justify-center gap-1.5 mb-1">
                          <div className={`w-1.5 h-1.5 rounded-full ${s.color}`} />
                          <span className="text-[10px] text-gray-500">{s.status}</span>
                        </div>
                        <span className="text-sm font-bold text-white font-mono">{s.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          SHADOW AS REFERENCE
      ════════════════════════════════════════════ */}
      <section id="shadow-reference" className="py-16 sm:py-20 px-4 border-b border-gray-100">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <div className="text-center mb-12">
              <motion.p variants={fadeUp} className="text-xs text-gray-400 uppercase tracking-widest mb-4">
                Reference Implementation
              </motion.p>
              <motion.h2 variants={fadeUp} custom={1} className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-5">
                We built it for ourselves first.
              </motion.h2>
              <motion.p variants={fadeUp} custom={2} className="text-gray-500 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
                Shadow's meeting copilot runs 5+ agents per session across Gemini, Deepgram, and 
                Recall.ai. We built Agent Margins to solve our own cost attribution problem &mdash; 
                and now we're opening it up.
              </motion.p>
            </div>

            <motion.div variants={fadeUp} custom={3} className="bg-gray-50 rounded-2xl border border-gray-100 p-6 sm:p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Shadow's cost breakdown per meeting</h3>
                  <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                    We needed to answer: what does each meeting cost per user? Which agents burn the 
                    most? How do we price fairly across tiers?
                  </p>
                  <div className="space-y-3">
                    {[
                      { service: "Recall.ai (bot infrastructure)", pct: 93, cost: "$1.86", color: "from-blue-500 to-indigo-500" },
                      { service: "Deepgram (transcription)", pct: 5, cost: "$0.10", color: "from-violet-500 to-purple-500" },
                      { service: "Gemini (LLM agents)", pct: 1, cost: "$0.02", color: "from-teal-500 to-cyan-500" },
                      { service: "RAG + grounding", pct: 1, cost: "$0.02", color: "from-amber-500 to-orange-500" },
                    ].map((item) => (
                      <div key={item.service}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-600">{item.service}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400">{item.pct}%</span>
                            <span className="text-xs font-mono font-medium text-gray-900">{item.cost}</span>
                          </div>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full bg-gradient-to-r ${item.color} rounded-full`}
                            style={{ width: `${Math.max(item.pct, 3)}%` }}
                          />
                        </div>
                      </div>
                    ))}
                    <div className="pt-3 mt-3 border-t border-gray-200 flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">Total per meeting</span>
                      <span className="text-lg font-bold text-gray-900 font-mono">$2.00</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-4 font-medium">What Agent Margins revealed</p>
                  <div className="space-y-4">
                    {[
                      { insight: "93% of cost is bot infrastructure, not LLM", action: "Negotiated volume discount with Recall.ai" },
                      { insight: "Power users cost 4x more than average", action: "Introduced usage tiers in Pro plan" },
                      { insight: "Fact-check agent is 3x costlier than others", action: "Optimized prompt + added caching" },
                      { insight: "Enterprise clients need per-seat billing", action: "Shipped usage-based invoicing in 2 days" },
                    ].map((item, i) => (
                      <div key={i} className="flex gap-3">
                        <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check className="w-3 h-3 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{item.insight}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{item.action}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          COMPETITIVE LANDSCAPE
      ════════════════════════════════════════════ */}
      <section id="compare" className="py-20 sm:py-28 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs text-gray-400 uppercase tracking-widest mb-4">Competitive Landscape</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-5">
              The only platform that closes the loop.
            </h2>
            <p className="text-gray-500 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
              Observability tools track costs but can't invoice. Billing platforms invoice but don't 
              understand AI costs. Agent Margins does both.
            </p>
          </div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="overflow-x-auto"
          >
            <div className="min-w-[640px]">
              {/* Header */}
              <div className="grid grid-cols-5 gap-3 mb-3">
                <div className="p-3" />
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-xs font-semibold text-gray-500">Helicone / Langfuse</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">LLM Observability</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-xs font-semibold text-gray-500">Metronome / Orb</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">Usage Billing</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-xs font-semibold text-gray-500">Revenium</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">AI Cost Control</p>
                </div>
                <div className="bg-[#0f172a] rounded-xl p-3 text-center">
                  <p className="text-xs font-semibold text-white">Agent Margins</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">Full Stack</p>
                </div>
              </div>

              {/* Rows */}
              {COMPARISON_DIMENSIONS.map((row, i) => (
                <motion.div
                  key={row.label}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  custom={i * 0.5}
                  className={`grid grid-cols-5 gap-3 ${i % 2 === 0 ? "bg-gray-50/50" : ""} rounded-lg`}
                >
                  <div className="p-3 flex items-center">
                    <span className="text-xs font-medium text-gray-700">{row.label}</span>
                  </div>
                  <div className="p-3 flex items-center justify-center">
                    <ComparisonCell value={row.helicone} />
                  </div>
                  <div className="p-3 flex items-center justify-center">
                    <ComparisonCell value={row.metronome} />
                  </div>
                  <div className="p-3 flex items-center justify-center">
                    <ComparisonCell value={row.revenium} />
                  </div>
                  <div className="p-3 flex items-center justify-center bg-[#0f172a]/5 rounded-lg">
                    <ComparisonCell value={row.margins} />
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-center text-xs text-gray-400 mt-8 max-w-lg mx-auto"
          >
            Observability tools (Helicone, Langfuse, LangSmith) excel at tracing and debugging. 
            Billing platforms (Metronome, Orb, Lago) excel at SaaS metering. Agent Margins bridges 
            the gap with AI-native cost attribution that feeds directly into invoicing.
          </motion.p>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          MARKET OPPORTUNITY
      ════════════════════════════════════════════ */}
      <section className="py-20 sm:py-28 px-4 bg-gray-50 border-y border-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs text-gray-400 uppercase tracking-widest mb-4">Market Opportunity</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-5">
              The market is massive.<br />The timing is now.
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { stat: "$11B", label: "Agentic AI market in 2026", sub: "Growing to $52B by 2030 (46% CAGR)", source: "Grand View Research" },
              { stat: "$7.4B", label: "Usage-based billing market", sub: "Reaching $11.5B by 2032", source: "GII Research" },
              { stat: "40%", label: "Enterprise apps with AI agents", sub: "By end of 2026, up from 5% in 2025", source: "Gartner" },
              { stat: "25%", label: "AI spend deferred to 2027", sub: "Due to inability to measure ROI", source: "Forrester" },
            ].map((item, i) => (
              <motion.div
                key={item.label}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-30px" }}
                variants={fadeUp}
                custom={i}
                className="bg-white rounded-2xl border border-gray-100 p-6 text-center hover:shadow-lg transition-all"
              >
                <p className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-2">
                  {item.stat}
                </p>
                <p className="text-sm font-medium text-gray-900 mb-1">{item.label}</p>
                <p className="text-xs text-gray-400 leading-relaxed">{item.sub}</p>
                <p className="text-[10px] text-gray-300 mt-3">{item.source}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={4}
            className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Target className="w-4 h-4 text-gray-500" />
                Where we play
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed mb-4">
                Agent Margins sits at the intersection of two fast-growing markets: AI infrastructure 
                ($11B) and usage-based billing ($7.4B). Neither existing category fully serves 
                Agentic AI companies.
              </p>
              <div className="flex flex-wrap gap-2">
                {["AI Dev Tools", "Billing Infrastructure", "FinOps for AI", "Revenue Operations"].map((tag) => (
                  <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full">{tag}</span>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4 text-gray-500" />
                Defensibility
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed mb-4">
                AI-native cost schemas are hard to retrofit. Billing platforms don't understand 
                token-level economics. Observability tools won't build invoicing. The full-stack 
                approach creates a natural moat.
              </p>
              <div className="flex flex-wrap gap-2">
                {["AI-native schemas", "Billing + observability lock-in", "Network effects on pricing data", "Open-source community"].map((tag) => (
                  <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full">{tag}</span>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          PRICING
      ════════════════════════════════════════════ */}
      <section id="pricing" className="py-20 sm:py-28 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs text-gray-400 uppercase tracking-widest mb-4">Pricing</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-5">
              Start free. Scale as you grow.
            </h2>
            <p className="text-gray-500 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
              Open-source core for cost attribution. Paid plans add invoicing, 
              pricing experimentation, and the finance dashboard.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Open Source */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={0}
              className="rounded-2xl border border-gray-100 p-6 bg-white hover:shadow-lg transition-shadow flex flex-col"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Open Source</h3>
              <p className="text-xs text-gray-400 mb-5">Self-host, full control</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-3xl font-bold text-gray-900">Free</span>
              </div>
              <p className="text-xs text-gray-400 mb-5">forever</p>
              <ul className="space-y-2.5 mb-6 flex-1 text-sm text-gray-600">
                {[
                  "10K events / month",
                  "Cost attribution + dashboards",
                  "LLM + external API tracking",
                  "Community support",
                  "Self-hosted",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <a
                href="https://github.com/nicoshadow"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-2.5 rounded-lg text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors text-center block"
              >
                Star on GitHub
              </a>
            </motion.div>

            {/* Growth */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={1}
              className="rounded-2xl border-2 border-gray-900 p-6 bg-white relative hover:shadow-xl transition-shadow flex flex-col"
            >
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-gray-900 text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                  Most Popular
                </span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Growth</h3>
              <p className="text-xs text-gray-400 mb-5">For teams shipping AI products</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-3xl font-bold text-gray-900">$99</span>
                <span className="text-gray-500 text-sm">/ month</span>
              </div>
              <p className="text-xs text-gray-400 mb-5">1M events included</p>
              <ul className="space-y-2.5 mb-6 flex-1 text-sm text-gray-600">
                {[
                  "1M events / month",
                  "Usage-based invoicing",
                  "3 pricing rules per client",
                  "Stripe integration",
                  "Finance dashboard",
                  "Email support",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <a
                href="https://wa.me/919582819710?text=Hi%2C%20I%27m%20interested%20in%20Agent%20Margins%20Growth%20plan"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-2.5 rounded-lg text-sm font-medium bg-gray-900 text-white hover:bg-gray-800 transition-colors text-center block"
              >
                Get Early Access
              </a>
            </motion.div>

            {/* Enterprise */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={2}
              className="rounded-2xl border border-gray-100 p-6 bg-white hover:shadow-lg transition-shadow flex flex-col"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Enterprise</h3>
              <p className="text-xs text-gray-400 mb-5">Custom everything</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-3xl font-bold text-gray-900">Custom</span>
              </div>
              <p className="text-xs text-gray-400 mb-5">volume pricing</p>
              <ul className="space-y-2.5 mb-6 flex-1 text-sm text-gray-600">
                {[
                  "Unlimited events",
                  "Unlimited pricing rules",
                  "Stripe + Razorpay",
                  "SSO + RBAC",
                  "99.9% SLA",
                  "Dedicated support",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <a
                href="https://wa.me/919582819710?text=Hi%2C%20I%27m%20interested%20in%20Agent%20Margins%20Enterprise%20plan"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-2.5 rounded-lg text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors text-center block"
              >
                Contact Sales
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          FINAL CTA
      ════════════════════════════════════════════ */}
      <section className="py-20 sm:py-28 px-4 bg-gray-50 border-t border-gray-100">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="max-w-3xl mx-auto text-center"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-5">
            Stop guessing your margins.
            <br />
            <span className="bg-gradient-to-r from-gray-900 via-gray-500 to-gray-900 bg-clip-text text-transparent">
              Start measuring them.
            </span>
          </h2>
          <p className="text-gray-500 text-base sm:text-lg max-w-xl mx-auto mb-10 leading-relaxed">
            Join the early access program. Ship cost attribution this week, 
            usage-based invoicing next week, and pricing experiments the week after.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://wa.me/919582819710?text=Hi%2C%20I%27m%20interested%20in%20Agent%20Margins"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-[#0f172a] text-white px-8 py-4 text-sm rounded-xl hover:bg-[#1e293b] transition-all font-medium flex items-center gap-2 shadow-lg shadow-gray-900/10 hover:shadow-xl hover:scale-[1.02]"
            >
              Get Early Access
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </a>
            <Link
              to="/conversational-intelligence"
              className="text-gray-600 hover:text-gray-900 px-8 py-4 text-sm font-medium flex items-center gap-2 transition-colors"
            >
              <ArrowUpRight className="w-4 h-4" />
              See Shadow's API
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-4 border-t border-gray-200 bg-white">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-black rounded-full" />
            <span className="text-sm font-semibold tracking-tight">
              <span className="text-gray-800">SHADOW</span>
              <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">LABS</span>
            </span>
          </div>
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <Link to="/" className="hover:text-gray-900 transition-colors">Home</Link>
            <Link to="/conversational-intelligence" className="hover:text-gray-900 transition-colors">API</Link>
            <Link to="/demo" className="hover:text-gray-900 transition-colors">Demo</Link>
            <Link to="/privacy" className="hover:text-gray-900 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-gray-900 transition-colors">Terms</Link>
          </div>
          <p className="text-xs text-gray-400">&copy; 2026 ShadowLabs</p>
        </div>
      </footer>
    </div>
  );
}
